'use client';

import Decimal from 'decimal.js';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import {
  useTradingDataLoading,
  useTradingTradeHistory,
} from '@/lib/components/Trading/TradingDataContext';
import { useSelectedOutcome } from '@/lib/store/order-form-store';
import { InlineEmpty } from '@/lib/ui-kit/inline-empty';
import { Text } from '@/lib/ui-kit/text';
import { formatCents, formatMoney } from '@/lib/utils/formatAmount';
import { formatTimeAgo } from '@/lib/utils/formatTime';

type HistoryItem = {
  id: string;
  action: 'Sold' | 'Bought';
  quantity: number;
  outcome: 'Yes' | 'No';
  price: number; // price in cents (0-100)
  total: number; // total amount in dollars
  timeAgo: string;
};

export const EventHistoryCard = () => {
  const t = useTranslations('history');
  const selectedOutcome = useSelectedOutcome();
  const outcomeId = selectedOutcome?.id ?? '';
  const resolutions = selectedOutcome?.r; // Array of Resolution objects with id and name

  // ✅ Use shared data from TradingDataContext (no duplicate requests)
  const data = useTradingTradeHistory(outcomeId);
  const isLoading = useTradingDataLoading(outcomeId);

  const items: HistoryItem[] = useMemo(() => {
    if (!data?.content) return [];

    return data.content.map((trade) => {
      const priceInCents = new Decimal(trade.price).mul(100).toNumber();
      const total = new Decimal(trade.price).mul(trade.quantity).toNumber();
      // Parse matchTime as UTC (ISO string should be in UTC)
      // Ensure the date is treated as UTC by appending 'Z' if not present
      const matchTimeUTC = trade.matchTime.endsWith('Z') ? trade.matchTime : trade.matchTime + 'Z';
      const matchDate = new Date(matchTimeUTC);
      // Use UTC time for now as well
      const nowUTC = new Date();
      const timeAgo = formatTimeAgo(matchDate, true, nowUTC);

      // Backend sends `s: "BUY" | "SELL"` per row (this user's side), which the
      // mapper still surfaces as `trade.sellerId` for legacy reasons.
      const action: 'Sold' | 'Bought' = trade.sellerId === 'SELL' ? 'Sold' : 'Bought';

      // WS rows carry position directly; REST rows derive it from assetId.
      let outcome: 'Yes' | 'No' = trade.position ?? 'Yes';
      if (!trade.position && resolutions && trade.assetId) {
        const resolution = resolutions.find((r) => r.id === trade.assetId);
        if (resolution) {
          outcome = resolution.isPositive ? 'Yes' : 'No';
        }
      }

      return {
        id: trade.id,
        action,
        quantity: trade.quantity,
        outcome,
        price: priceInCents,
        total,
        timeAgo,
      };
    });
  }, [data, resolutions]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center rounded-[10px] bg-v2-surface-secondary p-4">
        <Text variant="body-small" className="text-v2-text-secondary">
          {t('loading')}
        </Text>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-[10px] bg-v2-surface-secondary p-4">
        <InlineEmpty>{t('noHistory')}</InlineEmpty>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {items.map((item) => (
        <div
          key={item.id}
          className="flex items-center justify-between rounded-[10px] bg-v2-surface-secondary p-4"
        >
          <Text variant="body-small" className="text-v2-text-primary">
            {item.action === 'Sold' ? t('sold') : t('bought')} {item.quantity}{' '}
            <span className={item.outcome === 'Yes' ? 'text-v2-green' : 'text-red-400'}>
              {item.outcome}
            </span>{' '}
            {t('at')} {formatCents(item.price)}{' '}
            <span className="text-v2-text-secondary">
              ({formatMoney(item.total, { prefix: '$' })})
            </span>
          </Text>
          <Text variant="body-small" className="whitespace-nowrap text-v2-text-secondary">
            {t('ago', { time: item.timeAgo })}
          </Text>
        </div>
      ))}
    </div>
  );
};
